import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["webShareButton", "feedback"]
  static values = { calculatorType: String, shareUrl: String }

  connect() {
    if (typeof navigator.share === "function") {
      this.webShareButtonTarget.classList.remove("hidden")
    }
    this._cachedPermalink = null
    this._lastParamsKey = null
  }

  async copyLink() {
    const url = await this._ensureShareUrl()

    try {
      await navigator.clipboard.writeText(url)
    } catch {
      this._fallbackCopy(url)
    }
    this._showFeedback("Link copied!")
  }

  async openFacebook() {
    const url = await this._ensureShareUrl()
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "facebook")
  }

  async openLinkedIn() {
    const url = await this._ensureShareUrl()
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "linkedin")
  }

  async openWhatsApp() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`
    this._openWindow(shareUrl, "whatsapp")
  }

  async openX() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "x")
  }

  async openWebShare() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()

    try {
      await navigator.share({ title: document.title, text, url })
      this._showFeedback("Shared!")
    } catch (err) {
      if (err.name !== "AbortError") {
        this._showFeedback("Share failed")
      }
    }
  }

  // --- Private ---

  async _ensureShareUrl() {
    if (!this.hasShareUrlValue || !this.shareUrlValue) {
      return window.location.href
    }

    const currentParams = this._collectCurrentParams()
    const paramsKey = JSON.stringify(currentParams)

    if (this._cachedPermalink && this._lastParamsKey === paramsKey) {
      return this._cachedPermalink
    }

    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const response = await fetch(this.shareUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfMeta ? csrfMeta.content : ""
        },
        body: JSON.stringify(currentParams)
      })

      if (response.ok) {
        const data = await response.json()
        const permalink = `${window.location.origin}${data.url}`
        this._cachedPermalink = permalink
        this._lastParamsKey = paramsKey
        return permalink
      }
    } catch {
      // fall through
    }

    return window.location.href
  }

  _collectCurrentParams() {
    const type = this.calculatorTypeValue

    if (type === "debt_payoff") {
      return this._collectDebtParams()
    }
    if (type === "net_worth") {
      return this._collectNetWorthParams()
    }

    return this._collectSimpleParams()
  }

  _collectSimpleParams() {
    const params = new URLSearchParams(window.location.search)
    const result = {}
    for (const [key, value] of params.entries()) {
      result[key] = value
    }
    return result
  }

  _collectDebtParams() {
    const items = document.querySelectorAll(
      "[data-controller='debt-payoff-calculator'] [data-dynamic-list-target='item']"
    )
    const debts = Array.from(items).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      balance: item.querySelector("[data-field='balance']")?.value || "0",
      rate: item.querySelector("[data-field='rate']")?.value || "0",
      min_payment: item.querySelector("[data-field='min_payment']")?.value || "0"
    }))

    const extraEl = document.querySelector(
      "[data-controller='debt-payoff-calculator'] [data-debt-payoff-calculator-target='extraPayment']"
    )

    return {
      debts,
      extra_payment: extraEl?.value || "0"
    }
  }

  _collectNetWorthParams() {
    const assetItems = document.querySelectorAll(
      "[data-list-type='assets'] [data-dynamic-list-target='item']"
    )
    const liabilityItems = document.querySelectorAll(
      "[data-list-type='liabilities'] [data-dynamic-list-target='item']"
    )

    const assets = Array.from(assetItems).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      category: item.querySelector("[data-field='category']")?.value || "",
      value: item.querySelector("[data-field='value']")?.value || "0"
    }))

    const liabilities = Array.from(liabilityItems).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      category: item.querySelector("[data-field='category']")?.value || "",
      balance: item.querySelector("[data-field='balance']")?.value || "0"
    }))

    return { assets, liabilities }
  }

  _buildShareText() {
    switch (this.calculatorTypeValue) {
      case "budget_planner":
        return "Check out my budget breakdown on MoneySmart"
      case "debt_payoff":
        return "See how to pay off debt faster with MoneySmart"
      case "net_worth":
        return "Calculate your net worth with MoneySmart"
      case "savings_goal":
        return "Plan your savings goal with MoneySmart"
      case "compound_interest":
        return "See how compound interest grows your money"
      default:
        return "Check out this financial calculator on MoneySmart"
    }
  }

  _openWindow(url, name) {
    const width = 600
    const height = 500
    const left = (screen.width - width) / 2
    const top = (screen.height - height) / 2
    window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`)
  }

  _fallbackCopy(text) {
    const input = document.createElement("input")
    input.value = text
    document.body.appendChild(input)
    input.select()
    document.execCommand("copy")
    document.body.removeChild(input)
  }

  _showFeedback(text) {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.textContent = text
    this.feedbackTarget.classList.remove("hidden")
    clearTimeout(this._feedbackTimer)
    this._feedbackTimer = setTimeout(() => {
      this.feedbackTarget.classList.add("hidden")
    }, 2000)
  }
}
